import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, db } from './Login/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, where, query, doc, updateDoc, arrayUnion, getDoc, addDoc, deleteDoc, onSnapshot } from 'firebase/firestore';

const UserDataContext = createContext(null);

export const useUserData = () => useContext(UserDataContext);

export const UserDataProvider = ({ children }) => {
  const [authLoading, setAuthLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [friendsList, setFriendsList] = useState([]);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [games, setGames] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [gameInvites, setGameInvites] = useState([]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setAuthLoading(false);
      if (!user) {
        setUserData(null);
        setFriendsList([]);
        setIncomingRequests([]);
        setGameInvites([]);
        setGames([]);
        setGamesLoading(false);
        setDataLoaded(false);
      } else {
        // Re-arm the loading state so logging in after a logged-out visit
        // shows the spinner instead of a premature "No games yet".
        setGamesLoading(true);
      }
    });
    return () => unsubscribe();
  }, []);

  // Real-time listener for incoming game invites
  useEffect(() => {
    if (!currentUser) return;
    const invRef = collection(db, 'gameInvites');
    const q = query(invRef,
      where('toUid', '==', currentUser.uid),
      where('status', '==', 'pending')
    );
    const unsub = onSnapshot(q, (snap) => {
      setGameInvites(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => {
      console.error('Game invites listener error:', err);
    });
    return () => unsub();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || dataLoaded) return;

    const fetchAll = async () => {
      try {
        const usersRef = collection(db, 'backenddata');
        const reqRef = collection(db, 'friendRequests');

        const userQ = query(usersRef, where('email', '==', currentUser.email));
        const userSnap = await getDocs(userQ);
        const userList = userSnap.docs.map(d => d.data());
        setUserData(userList);

        let friendsUsernames = [...(userList[0]?.Friends || [])];

        // Process accepted outgoing requests: add friend + cleanup.
        // Isolated try/catch: guests have no backenddata doc, so updateDoc
        // throws — that must not abort the rest of the dashboard load.
        try {
          const outQ = query(reqRef,
            where('fromUid', '==', currentUser.uid),
            where('status', '==', 'accepted')
          );
          const outSnap = await getDocs(outQ);
          const myDocRef = doc(db, 'backenddata', currentUser.uid);
          for (const d of outSnap.docs) {
            const req = d.data();
            if (!friendsUsernames.includes(req.toUsername)) {
              friendsUsernames.push(req.toUsername);
              await updateDoc(myDocRef, { Friends: arrayUnion(req.toUsername) });
            }
            await deleteDoc(doc(reqRef, d.id));
          }
        } catch (e) {
          console.error('Error processing accepted friend requests:', e);
        }

        // Load friend profiles
        if (friendsUsernames.length > 0) {
          const batched = [];
          for (let i = 0; i < friendsUsernames.length; i += 30) {
            const batch = friendsUsernames.slice(i, i + 30);
            const friendsQ = query(usersRef, where('username', 'in', batch));
            const friendsSnap = await getDocs(friendsQ);
            friendsSnap.docs.forEach(d => batched.push(d.data()));
          }
          setFriendsList(batched);
        }

        // Load incoming pending requests
        const inQ = query(reqRef,
          where('toUid', '==', currentUser.uid),
          where('status', '==', 'pending')
        );
        const inSnap = await getDocs(inQ);
        const requests = inSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        if (requests.length > 0) {
          const senderUids = requests.map(r => r.fromUid);
          for (let i = 0; i < senderUids.length; i += 30) {
            const batch = senderUids.slice(i, i + 30);
            const picSnap = await getDocs(
              query(usersRef, where('__name__', 'in', batch))
            );
            const picMap = {};
            picSnap.forEach(d => { picMap[d.id] = d.data().profilePicture || null; });
            requests.forEach(r => {
              if (picMap[r.fromUid] !== undefined) r.fromPic = picMap[r.fromUid];
            });
          }
        }
        setIncomingRequests(requests);

        // Load games
        const userGames = userList[0]?.games || [];
        const gamesList = [];
        const opponentUids = new Set();
        if (userGames.length > 0) {
          const gamesRef = collection(db, 'games');
          for (let i = 0; i < userGames.length; i += 30) {
            const batch = userGames.slice(i, i + 30);
            const batchQ = query(gamesRef, where('game.gameId', 'in', batch));
            const batchSnap = await getDocs(batchQ);
            batchSnap.forEach((d) => {
              const data = d.data();
              const members = data.game?.members || [];
              const me = members.find(m => m.uid === currentUser.uid);
              const opponent = members.find(m => m.uid !== currentUser.uid);

              let result = 'unknown';
              if (data.winner) {
                if (me && data.winner === me.name) result = 'win';
                else if (data.winner === 'Draw') result = 'draw';
                else result = 'loss';
              }
              if (opponent?.uid) opponentUids.add(opponent.uid);
              gamesList.push({
                gameId: data.game.gameId,
                opponentName: opponent?.name || 'Unknown',
                opponentUid: opponent?.uid || null,
                result,
                status: data.game?.status || null,
                fen: data.gameData || null,
                memberPiece: me?.piece || 'w',
              });
            });
          }

          const opponentPics = {};
          const uidArray = [...opponentUids];
          for (let i = 0; i < uidArray.length; i += 30) {
            const uidBatch = uidArray.slice(i, i + 30);
            const picSnap = await getDocs(
              query(usersRef, where('__name__', 'in', uidBatch.map(u => u)))
            );
            picSnap.forEach((d) => {
              const pData = d.data();
              opponentPics[d.id] = pData.profilePicture || null;
            });
          }
          gamesList.forEach(g => {
            g.opponentPic = g.opponentUid ? (opponentPics[g.opponentUid] || null) : null;
          });
        }
        gamesList.sort((a, b) => {
          const tsA = parseInt(a.gameId.split('_')[1]) || 0;
          const tsB = parseInt(b.gameId.split('_')[1]) || 0;
          return tsB - tsA;
        });
        setGames(gamesList);
        setDataLoaded(true);
      } catch (e) {
        console.error('Error fetching user data:', e);
      } finally {
        setGamesLoading(false);
      }
    };

    fetchAll();
  }, [currentUser, dataLoaded]);

  const sendFriendRequest = useCallback(async (friendUsername) => {
    if (!currentUser) throw new Error('Not logged in');
    const usersRef = collection(db, 'backenddata');
    const reqRef = collection(db, 'friendRequests');

    const friendQ = query(usersRef, where('username', '==', friendUsername));
    const friendSnap = await getDocs(friendQ);
    if (friendSnap.empty) throw new Error('No user found with that username.');

    const friendDoc = friendSnap.docs[0];
    const friendData = friendDoc.data();
    const friendUid = friendDoc.id;

    if (friendUid === currentUser.uid) throw new Error("You can't add yourself.");

    const myDocRef = doc(usersRef, currentUser.uid);
    const myDoc = await getDoc(myDocRef);
    // Anonymous guests have no profile doc — they can't be friended back and
    // accepting their request would poison the recipient's Friends array.
    if (!myDoc.exists() || !myDoc.data()?.username) {
      throw new Error('You need an account to add friends.');
    }
    const myFriends = myDoc.data()?.Friends || [];
    if (myFriends.includes(friendUsername)) throw new Error('Already friends.');

    // Check if I already sent them a request
    const existingOutSnap = await getDocs(query(reqRef,
      where('fromUid', '==', currentUser.uid),
      where('toUid', '==', friendUid)
    ));
    if (!existingOutSnap.empty) throw new Error('Friend request already sent.');

    // If they already sent me a request, auto-accept it
    const existingInSnap = await getDocs(query(reqRef,
      where('fromUid', '==', friendUid),
      where('toUid', '==', currentUser.uid),
      where('status', '==', 'pending')
    ));
    if (!existingInSnap.empty) {
      const reqDoc = existingInSnap.docs[0];
      await updateDoc(doc(reqRef, reqDoc.id), { status: 'accepted' });
      await updateDoc(myDocRef, { Friends: arrayUnion(friendUsername) });
      setFriendsList(prev => [...prev, friendData]);
      setIncomingRequests(prev => prev.filter(r => r.id !== reqDoc.id));
      return 'accepted';
    }

    const myData = myDoc.data();
    await addDoc(reqRef, {
      fromUid: currentUser.uid,
      fromUsername: myData?.username || 'Unknown',
      toUid: friendUid,
      toUsername: friendUsername,
      status: 'pending',
    });

    return 'sent';
  }, [currentUser]);

  const acceptRequest = useCallback(async (requestId, fromUsername) => {
    if (!currentUser) throw new Error('Not logged in');
    const reqRef = collection(db, 'friendRequests');
    const usersRef = collection(db, 'backenddata');

    await updateDoc(doc(reqRef, requestId), { status: 'accepted' });
    await updateDoc(doc(usersRef, currentUser.uid), {
      Friends: arrayUnion(fromUsername),
    });

    setIncomingRequests(prev => prev.filter(r => r.id !== requestId));

    const friendQ = query(usersRef, where('username', '==', fromUsername));
    const friendSnap = await getDocs(friendQ);
    if (!friendSnap.empty) {
      setFriendsList(prev => [...prev, friendSnap.docs[0].data()]);
    }
  }, [currentUser]);

  const rejectRequest = useCallback(async (requestId) => {
    if (!currentUser) throw new Error('Not logged in');
    await deleteDoc(doc(db, 'friendRequests', requestId));
    setIncomingRequests(prev => prev.filter(r => r.id !== requestId));
  }, [currentUser]);

  const acceptGameInvite = useCallback(async (invite) => {
    if (!currentUser) throw new Error('Not logged in');
    await deleteDoc(doc(db, 'gameInvites', invite.id));
    return invite.gameId;
  }, [currentUser]);

  const declineGameInvite = useCallback(async (invite) => {
    if (!currentUser) throw new Error('Not logged in');
    await updateDoc(doc(db, 'gameInvites', invite.id), { status: 'declined' });
  }, [currentUser]);

  const uploadProfilePicture = useCallback(async (file) => {
    if (!currentUser) throw new Error('Not logged in');

    const dataUrl = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const size = 200;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        const min = Math.min(img.width, img.height);
        const sx = (img.width - min) / 2;
        const sy = (img.height - min) / 2;
        ctx.drawImage(img, sx, sy, min, min, 0, 0, size, size);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });

    const userDocRef = doc(db, 'backenddata', currentUser.uid);
    await updateDoc(userDocRef, { profilePicture: dataUrl });
    setUserData(prev => prev ? prev.map(u => ({ ...u, profilePicture: dataUrl })) : prev);
    return dataUrl;
  }, [currentUser]);

  const refreshData = useCallback(() => {
    setDataLoaded(false);
    setGamesLoading(true);
  }, []);

  return (
    <UserDataContext.Provider value={{
      authLoading,
      currentUser,
      userData,
      friendsList,
      incomingRequests,
      gameInvites,
      games,
      gamesLoading,
      sendFriendRequest,
      acceptRequest,
      rejectRequest,
      acceptGameInvite,
      declineGameInvite,
      uploadProfilePicture,
      refreshData,
    }}>
      {children}
    </UserDataContext.Provider>
  );
};
